import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, forwardRef, Directive, Self, HostBinding, Input, HostListener, Component, ChangeDetectionStrategy, ContentChild, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { FormControl } from '@angular/forms';
import { Subject, merge } from 'rxjs';
import { tap } from 'rxjs/operators';

const SI_VALIDATION_ERROR_RESOLVER_MAP = new InjectionToken('si.validation.error.resolver.map');
/**
 * Service used by {@link SiFormGroupComponent} to resolve validation errors to human readable text.
 */
class SiValidationErrorResolverService {
    constructor(resolverMap) {
        this.resolverMap = resolverMap;
    }
    resolve(errors, componentMap) {
        if (errors) {
            return Object.entries(errors)
                .map(([key, body]) => this.resolveKey(key, body, componentMap))
                .filter(e => e)
                .join(', ');
        }
        return null;
    }
    resolveKey(errorKey, errorBody, componentMap) {
        let res = null;
        if (componentMap) {
            res = componentMap[errorKey];
        }
        if (!res && this.resolverMap) {
            res = this.resolverMap[errorKey];
        }
        if (res) {
            if (typeof res === 'string') {
                return res;
            }
            else {
                return res(errorBody);
            }
        }
        return null;
    }
}
SiValidationErrorResolverService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiValidationErrorResolverService, deps: [{ token: SI_VALIDATION_ERROR_RESOLVER_MAP, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
SiValidationErrorResolverService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiValidationErrorResolverService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiValidationErrorResolverService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SI_VALIDATION_ERROR_RESOLVER_MAP]
                }] }]; } });

class SiFormControlAccessor {
}

class SiFormControlDirective {
    constructor(_ngControl, controlElementRef, renderer2) {
        this._ngControl = _ngControl;
        this.controlElementRef = controlElementRef;
        this.renderer2 = renderer2;
        this.formControl = true;
        this.placeholder = ' ';
        this.focussed = false;
        this._focussedChanges = new Subject();
    }
    ngOnInit() {
        if (this.controlElementRef.nativeElement instanceof HTMLTextAreaElement) {
            const siOverlapFixElement = this.renderer2.createElement('span');
            this.renderer2.addClass(siOverlapFixElement, 'si-label-overlap-fix');
            this.siOverlapFixElementDestroyer = this.renderer2.listen(siOverlapFixElement, 'click', () => this.controlElementRef.nativeElement.focus());
            this.renderer2.appendChild(this.renderer2.parentNode(this.controlElementRef.nativeElement), siOverlapFixElement);
        }
    }
    get ngControl() {
        return this._ngControl;
    }
    get focussedChanges() {
        return this._focussedChanges.asObservable();
    }
    ngOnDestroy() {
        this._focussedChanges.complete();
        if (this.siOverlapFixElementDestroyer) {
            this.siOverlapFixElementDestroyer();
        }
    }
    blur() {
        this.focussed = false;
        this._focussedChanges.next(this.focussed);
    }
    focus() {
        this.focussed = true;
        this._focussedChanges.next(this.focussed);
    }
}
SiFormControlDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFormControlDirective, deps: [{ token: i1.NgControl, optional: true, self: true }, { token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
SiFormControlDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiFormControlDirective, selector: "input[siFormControl], textarea[siFormControl]", inputs: { placeholder: "placeholder", id: "id", type: "type" }, host: { listeners: { "blur": "blur()", "focus": "focus()" }, properties: { "class.form-control": "this.formControl", "placeholder": "this.placeholder", "attr.id": "this.id", "attr.type": "this.type" } }, providers: [{ provide: SiFormControlAccessor, useExisting: forwardRef(() => SiFormControlDirective) }], exportAs: ["siFormControl"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFormControlDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[siFormControl], textarea[siFormControl]',
                    exportAs: 'siFormControl',
                    // eslint-disable-next-line @angular-eslint/no-forward-ref
                    providers: [{ provide: SiFormControlAccessor, useExisting: forwardRef(() => SiFormControlDirective) }],
                }]
        }], ctorParameters: function () { return [{ type: i1.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }, { type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { formControl: [{
                type: HostBinding,
                args: ['class.form-control']
            }], placeholder: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['placeholder']
            }], id: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.id']
            }], type: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.type']
            }], blur: [{
                type: HostListener,
                args: ['blur']
            }], focus: [{
                type: HostListener,
                args: ['focus']
            }] } });

/**
 * Container for controls. It takes care of resizing the label and displays error messages if configured.
 * It expects to have [si] as content child.
 */
class SiFormGroupComponent {
    constructor(errorResolver, changeDetector, renderer2) {
        this.errorResolver = errorResolver;
        this.changeDetector = changeDetector;
        this.renderer2 = renderer2;
        this.error = null;
        this._inputGroup = true;
        this.required = false;
        this.validationControl = new FormControl('');
    }
    ngAfterContentInit() {
        if (!this.control) {
            console.error('There is an error with the si-form-group component;\n' +
                'There is no child input or input is missing the siFormControl attribute.');
            return;
        }
        if (!this.control.ngControl) {
            console.error('There is an error with the si-form-group component;\n' +
                'The child (eg. input) has no ngModel or formControl attached.');
            return;
        }
        this.formControlID = this.control.id ? this.control.id : this.control.ngControl.name ?? undefined;
        this.formControlTouched = this.control.ngControl.touched;
        // initial calculation
        this.recalculate();
        this.checkRequiredValidation();
        // ChangeDetection = OnPush for better performance
        // --> recalculate on every focus or value change
        this.subscription = merge(this.control.ngControl.valueChanges, this.control.focussedChanges, this.control.ngControl.statusChanges)
            .pipe(tap(() => this.recalculate()))
            .subscribe(() => this.changeDetector.markForCheck());
    }
    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
    ngDoCheck() {
        // This cannot be handled via changeListener as none is provided by Angular
        if (this.formControlTouched !== undefined && this.control.ngControl.touched !== this.formControlTouched) {
            this.formControlTouched = this.control.ngControl.touched;
            this.changeDetector.markForCheck();
        }
        this.checkRequiredValidation();
    }
    checkRequiredValidation() {
        if (this.control?.ngControl?.control?.validator) {
            // Test the validator(s) of the control with an empty FormControl. If the validation result contains required
            // it can be assumed that  a value is required
            const validators = this.control.ngControl.control.validator(this.validationControl);
            this.required = validators?.required;
        }
        else {
            this.required = false;
        }
        this.changeDetector.markForCheck();
    }
    recalculate() {
        this.error = this.errorResolver.resolve(this.control.ngControl.errors, this.errorResolverMap);
        if (this.error && this.showError) {
            this.renderer2.setAttribute(this.control.controlElementRef.nativeElement, 'aria-describedby', this.formControlID + '_form-feedback');
        }
    }
    get showError() {
        return this.control?.ngControl?.invalid && this.control?.ngControl?.touched;
    }
}
SiFormGroupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFormGroupComponent, deps: [{ token: SiValidationErrorResolverService }, { token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
SiFormGroupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiFormGroupComponent, selector: "si-form-group", inputs: { label: "label", errorResolverMap: "errorResolverMap" }, host: { properties: { "class.input-group": "this._inputGroup", "class.si-required": "this.required", "class.si-invalid": "this.showError" } }, queries: [{ propertyName: "control", first: true, predicate: SiFormControlAccessor, descendants: true, static: true }], ngImport: i0, template: "<div class=\"form-floating flex-grow-1\">\n    <ng-content select=\"[siFormControl],si-dropdown,si-chips-input\"></ng-content>\n    <label [attr.for]=\"formControlID\">\n        {{label}}\n    </label>\n</div>\n<ng-content></ng-content>\n<ng-content select=\".form-feedback\"></ng-content>\n<div [id]=\"formControlID + '_form-feedback'\" class=\"form-feedback\" *ngIf=\"showError\">\n    <div class=\"invalid-feedback\">\n        {{error}}\n    </div>\n</div>\n", styles: [":host.si-required label:after{content:\"*\";color:var(--form-invalid-color);font-weight:700;padding-left:3px}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFormGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-form-group', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"form-floating flex-grow-1\">\n    <ng-content select=\"[siFormControl],si-dropdown,si-chips-input\"></ng-content>\n    <label [attr.for]=\"formControlID\">\n        {{label}}\n    </label>\n</div>\n<ng-content></ng-content>\n<ng-content select=\".form-feedback\"></ng-content>\n<div [id]=\"formControlID + '_form-feedback'\" class=\"form-feedback\" *ngIf=\"showError\">\n    <div class=\"invalid-feedback\">\n        {{error}}\n    </div>\n</div>\n", styles: [":host.si-required label:after{content:\"*\";color:var(--form-invalid-color);font-weight:700;padding-left:3px}\n"] }]
        }], ctorParameters: function () { return [{ type: SiValidationErrorResolverService }, { type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }]; }, propDecorators: { control: [{
                type: ContentChild,
                args: [SiFormControlAccessor, { static: true }]
            }], label: [{
                type: Input
            }], errorResolverMap: [{
                type: Input
            }], _inputGroup: [{
                type: HostBinding,
                args: ['class.input-group']
            }], required: [{
                type: HostBinding,
                args: ['class.si-required']
            }], showError: [{
                type: HostBinding,
                args: ['class.si-invalid']
            }] } });

class SiFormGroupModule {
}
SiFormGroupModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFormGroupModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiFormGroupModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiFormGroupModule, declarations: [SiFormControlDirective, SiFormGroupComponent], imports: [CommonModule], exports: [SiFormControlDirective, SiFormGroupComponent] });
SiFormGroupModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFormGroupModule, providers: [SiValidationErrorResolverService], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiFormGroupModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiFormControlDirective, SiFormGroupComponent],
                    imports: [CommonModule],
                    exports: [SiFormControlDirective, SiFormGroupComponent],
                    providers: [SiValidationErrorResolverService],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SI_VALIDATION_ERROR_RESOLVER_MAP, SiFormControlAccessor, SiFormControlDirective, SiFormGroupComponent, SiFormGroupModule, SiValidationErrorResolverService };
